import * as React from 'react';
import Image from 'next/image';
import { cn, formatNumber } from '@/lib/utils';
import { asset } from '@/lib/asset';

/**
 * Coin amounts — one icon, one number format.
 *
 * The app rendered coins three ways: `assets/icons/coins.webp` (17 files in
 * the Next webapp, as .png),
 * lucide's generic `<Coins>` (49 files), and a hand-rolled outline SVG
 * copy-pasted verbatim into five rewards files. It also used three font
 * treatments and, in /earn only, an invented `c` suffix ("+200c").
 *
 * The PNG wins. It is the brand asset — the same mark on the store, the
 * balance pill, the nav and every purchase surface, i.e. every screen where
 * the player's money actually moves. A generic lucide glyph on the rewards
 * screens told the player that /earn coins and /store coins were two different
 * currencies. The hand-rolled SVG was nobody's decision; it was a copy.
 *
 * The one thing the PNG cannot do is take `currentColor`, which the SVG copies
 * used to tint themselves gold or cream per context. That tint is not a loss:
 * the asset is already the coin's own colour, and a coin that changes colour to
 * match its label is a coin the player has to re-read.
 */
export type CoinSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE: Record<CoinSize, { icon: string; px: number; gap: string }> = {
  xs: { icon: 'w-2.5 h-2.5', px: 10, gap: 'gap-0.5' },
  sm: { icon: 'w-3 h-3', px: 12, gap: 'gap-0.5' },
  md: { icon: 'w-4 h-4', px: 16, gap: 'gap-1.5' },
  lg: { icon: 'w-5 h-5', px: 20, gap: 'gap-2' },
};

/**
 * The coin mark on its own, for places that lay out their own number.
 * `unoptimized` matches every other usage of this asset in the app — it is a
 * tiny fixed-size image that the optimizer cannot improve. `next/image` here
 * is the local shim (src/lib/next-shims/image.tsx) over a native <img>, and
 * `asset()` is required because this bundle is served from a CDN subdirectory
 * whose path is not known at build time.
 */
export function CoinGlyph({ className, size = 'sm' }: { className?: string; size?: CoinSize }) {
  const s = SIZE[size];
  return (
    <Image
      src={asset('assets/icons/coins.webp')}
      alt=""
      aria-hidden="true"
      width={s.px}
      height={s.px}
      unoptimized
      className={cn(s.icon, className)}
    />
  );
}

export interface CoinProps {
  amount: number;
  size?: CoinSize;
  /** Prefix a `+` — for amounts being awarded rather than held. */
  signed?: boolean;
  /** Compact form (1.2K) instead of the full grouped number (1,200). */
  compact?: boolean;
  showIcon?: boolean;
  className?: string;
  iconClassName?: string;
  /**
   * When set, the number is wrapped in its own span carrying these classes.
   * When omitted it renders as a bare text node, so migrating a call site that
   * styled icon and number together changes no DOM.
   */
  valueClassName?: string;
}

export function Coin({
  amount,
  size = 'sm',
  signed = false,
  compact = false,
  showIcon = true,
  className,
  iconClassName,
  valueClassName,
}: CoinProps) {
  const s = SIZE[size];
  const text = `${signed && amount >= 0 ? '+' : ''}${compact ? formatNumber(amount) : amount.toLocaleString()}`;

  return (
    // `tabular-nums` sits on the wrapper so it applies whether the number is a
    // bare text node or a wrapped span. Coin totals count up and tick down
    // live; proportional figures make the row shuffle while they do it.
    <span className={cn('inline-flex items-center tabular-nums', s.gap, className)}>
      {showIcon && <CoinGlyph size={size} className={iconClassName} />}
      {valueClassName ? <span className={valueClassName}>{text}</span> : text}
    </span>
  );
}
